import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { X, Upload, Image as ImageIcon } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useState, useRef, useEffect } from "react";
import { uploadImageToStorage } from "@/lib/place-image-utils";

interface PlacePhotosProps {
  photos: string[];
  setPhotos: (photos: string[]) => void;
  photoInput: string;
  setPhotoInput: (value: string) => void;
  isExistingPlace?: boolean;
  placeName?: string;
}

export function PlacePhotos({
  photos,
  setPhotos,
  photoInput,
  setPhotoInput,
  isExistingPlace = false,
  placeName = "Place"
}: PlacePhotosProps) {
  const { toast } = useToast();
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [imageStatuses, setImageStatuses] = useState<Record<string, 'loading' | 'loaded' | 'error'>>({});

  // Initialize image statuses for all photos
  useEffect(() => {
    if (photos.length > 0) {
      const newStatuses: Record<string, 'loading' | 'loaded' | 'error'> = {};
      photos.forEach(photo => {
        if (!imageStatuses[photo]) {
          newStatuses[photo] = 'loading';
        }
      });
      
      if (Object.keys(newStatuses).length > 0) {
        setImageStatuses(prev => ({ ...prev, ...newStatuses }));
      }
    }
  }, [photos]);

  // Clean up potentially malformed URLs
  const cleanImageUrl = (url: string): string => {
    if (!url) return '';
    
    try {
      // Fix incorrectly formatted storage URLs
      if (url.includes('supabase.co/storage') && url.includes('%252F')) {
        return url.replace(/%252F/g, '%2F');
      }
      
      // Remove API keys and tokens if present
      if (url.includes('key=') || url.includes('token=')) {
        const urlObj = new URL(url);
        urlObj.searchParams.delete('key');
        urlObj.searchParams.delete('token');
        return urlObj.toString();
      }
      
      return url;
    } catch (e) {
      console.warn('Error cleaning URL', e);
      return url;
    }
  };

  const isValidUrl = (string: string) => {
    try {
      new URL(string);
      return true;
    } catch (_) {
      return false;
    }
  };

  const handleAddPhoto = () => {
    if (photoInput && isValidUrl(photoInput)) {
      setPhotos([...photos, photoInput]);
      setPhotoInput('');
    } else {
      toast({
        title: "Invalid URL",
        description: "Please enter a valid image URL",
        variant: "destructive",
      });
    }
  };

  const handleRemovePhoto = (index: number) => {
    const newPhotos = photos.filter((_, i) => i !== index);
    setPhotos(newPhotos);
    
    // Clean up image statuses for removed photos
    const newStatuses = { ...imageStatuses };
    const removedPhoto = photos[index];
    if (removedPhoto && newStatuses[removedPhoto]) {
      delete newStatuses[removedPhoto];
      setImageStatuses(newStatuses);
    }
  };

  const handleImageLoad = (photo: string) => {
    setImageStatuses(prev => ({ ...prev, [photo]: 'loaded' }));
  };

  const handleImageError = (photo: string) => {
    setImageStatuses(prev => ({ ...prev, [photo]: 'error' }));
    console.error('Image failed to load:', photo);
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    setIsUploading(true);
    
    try {
      const newPhotos = [...photos];
      
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        
        toast({
          title: "Uploading image",
          description: `${file.name} (${(file.size / (1024 * 1024)).toFixed(2)} MB)`,
        });
        
        const imageUrl = await uploadImageToStorage(file, placeName);
        
        if (imageUrl) {
          newPhotos.push(imageUrl);
          setImageStatuses(prev => ({ ...prev, [imageUrl]: 'loading' }));
          
          toast({
            title: "Upload complete",
            description: "Image added to place photos",
          });
        } else {
          toast({
            title: "Upload failed",
            description: "Could not upload image",
            variant: "destructive",
          });
        }
      }
      
      setPhotos(newPhotos);
      
      // Clear the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (error) {
      console.error('Error uploading images:', error);
      toast({
        title: "Upload failed",
        description: "An error occurred while uploading images",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };
  
  const triggerFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  return (
    <div className="space-y-2">
      <Label>Photos</Label>
      
      {/* Hidden file input */}
      <input 
        type="file" 
        ref={fileInputRef} 
        className="hidden" 
        accept="image/*" 
        multiple 
        onChange={handleFileUpload}
        disabled={isExistingPlace || isUploading}
      />
      
      <div className="flex flex-col space-y-2">
        <div className="flex gap-2">
          <Input
            value={photoInput}
            onChange={(e) => setPhotoInput(e.target.value)}
            placeholder="Enter image URL"
            disabled={isExistingPlace || isUploading}
            className={isExistingPlace ? "bg-gray-50" : ""}
          />
          <Button 
            type="button" 
            onClick={handleAddPhoto} 
            disabled={isExistingPlace || isUploading || !photoInput}
          >
            Add URL
          </Button>
        </div>
        
        <Button
          type="button" 
          variant="outline"
          className="flex gap-2 w-full"
          onClick={triggerFileInput}
          disabled={isExistingPlace || isUploading}
        >
          <Upload className="w-4 h-4" />
          {isUploading ? "Uploading..." : "Upload from device"}
        </Button>
      </div>
      
      {photos.length > 0 && (
        <div className="grid grid-cols-2 gap-2 mt-2">
          {photos.map((photo, index) => {
            const cleanedPhotoUrl = cleanImageUrl(photo);
            return (
              <div key={index} className="relative group">
                <div className="relative h-32 bg-gray-100 rounded-md overflow-hidden">
                  {/* Loading placeholder */}
                  {(!imageStatuses[photo] || imageStatuses[photo] === 'loading') && (
                    <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
                      <ImageIcon className="w-6 h-6 text-gray-400 animate-pulse" />
                    </div>
                  )}
                  
                  {/* Error placeholder */}
                  {imageStatuses[photo] === 'error' && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-100">
                      <ImageIcon className="w-6 h-6 text-gray-400" />
                      <span className="text-xs text-gray-500 mt-1">Image unavailable</span>
                      <span className="text-[10px] text-gray-400 mt-1 px-2 text-center truncate">{cleanedPhotoUrl.substring(0, 20)}</span>
                    </div>
                  )}
                  
                  {/* Actual image */}
                  <img
                    src={cleanedPhotoUrl}
                    alt={`Photo ${index + 1}`}
                    className={`w-full h-full object-cover transition-opacity duration-300 ${
                      imageStatuses[photo] === 'loaded' ? 'opacity-100' : 'opacity-0'
                    }`}
                    onLoad={() => handleImageLoad(photo)}
                    onError={() => handleImageError(photo)}
                  />
                </div>
                
                <button
                  onClick={() => handleRemovePhoto(index)}
                  className={`absolute top-1 right-1 p-1 bg-black/50 rounded-full ${isExistingPlace ? 'hidden' : 'opacity-0 group-hover:opacity-100 transition-opacity'}`}
                  disabled={isExistingPlace}
                  type="button"
                  aria-label="Remove photo"
                >
                  <X className="w-4 h-4 text-white" />
                </button>
              </div>
            );
          })}
        </div>
      )}
      
      {photos.length === 0 && (
        <div className="flex items-center justify-center py-8 border-2 border-dashed rounded-md border-gray-300 bg-gray-50">
          <div className="flex flex-col items-center space-y-2 text-gray-500">
            <ImageIcon className="w-8 h-8" />
            <p className="text-sm">No photos added yet</p>
            {!isExistingPlace && (
              <p className="text-xs text-center">
                Add a URL above or upload images from your device
              </p>
            )}
          </div>
        </div>
      )}
      
      {isExistingPlace && photos.length > 0 && (
        <p className="text-xs text-muted-foreground italic">
          Photos are shared with the original place and cannot be modified.
        </p>
      )}
    </div>
  );
}
